import React, { FormEvent, useEffect, useState } from "react";
import { Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { requestPasswordReset, requestPasswordResetEmail } from "../../../API/requestPasswordReset";
import Logo from "../../../assets/logo_siscap.svg";
import CpfField from "../../atoms/CpfField/CpfField";
import EmailAutocomplete from "../../atoms/EmailAutocomplete/EmailAutoComplete";
import styles from "./RecuperarSenha.module.css";

type RecoveryStep = "cpf" | "email";

const RecuperarSenha: React.FC = () => {
  const [cpf, setCPF] = useState("");
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [step, setStep] = useState<RecoveryStep>("cpf");
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleCpfSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!cpf) {
      setMsgError("Por favor, informe o CPF.");
      return;
    }

    setLoading(true);
    setMsgError(null);

    const result = await requestPasswordResetEmail(cpf);

    if (result.error) {
      setMsgError(result.message);
    } else {
      setMaskedEmail(result.email);
      setStep("email");
    }

    setLoading(false);
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) {
      setMsgError("Por favor, informe o e-mail.");
      return;
    }

    setLoading(true);
    setMsgError(null);

    const result = await requestPasswordReset(cpf, email);

    if (result.error) {
      setMsgError(result.message);
    } else {
      setMsgSuccess(result.message);
    }

    setLoading(false);
  };

  const handleChangeCpf = () => {
    setEmail("");
    setMaskedEmail("");
    setMsgError(null);
    setStep("cpf");
  };

  useEffect(() => {
    if (!msgError) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setMsgError(null), 5000);

    return () => window.clearTimeout(timeoutId);
  }, [msgError]);

  const handleSubmit = step === "cpf" ? handleCpfSubmit : handleEmailSubmit;
  const submitLabel = step === "cpf" ? "Continuar" : "Enviar";
  const loadingLabel = step === "cpf" ? "Consultando..." : "Enviando...";

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.cardRight}>
          <div className={styles.divImage3}>
            <img src={Logo} alt="Cadeado SISCAP" className={styles.cadeado} />
          </div>

          <h2 className={styles.title}>Esqueceu a Senha</h2>

          <form className={styles.form} onSubmit={handleSubmit}>
            {msgSuccess && (
              <div className={styles.inputGroup}>
                <Alert severity="success">{msgSuccess}</Alert>
              </div>
            )}

            {msgError && (
              <div className={styles.inputGroup}>
                <Alert severity="error">{msgError}</Alert>
              </div>
            )}

            {!msgSuccess && step === "cpf" && (
              <div className={styles.inputGroup}>
                <label className={styles.titleInput}>CPF</label>
                <CpfField
                  size="small"
                  placeholder=""
                  variant="standard"
                  autoFocus={false}
                  value={cpf}
                  onUpdate={setCPF}
                  customClassName={styles.input}
                  numericKeyboard
                />
              </div>
            )}

            {!msgSuccess && step === "email" && (
              <>
                <div className={styles.emailConfirmation}>
                  <span>E-mail cadastrado</span>
                  <strong>{maskedEmail}</strong>
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.titleInput}>Confirme seu e-mail</label>
                  <EmailAutocomplete
                    label=""
                    value={email}
                    onChange={setEmail}
                    inputProps={{ disabledUnderline: true }}
                    customClassName={styles.input}
                  />
                </div>
              </>
            )}

            <div className={styles.blButtons}>
              {!msgSuccess && (
                <div className={styles.buttonGroup}>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`${styles.button} ${loading ? styles.buttonLoad : ""}`}
                  >
                    {loading ? (
                      <>
                        <div className={styles.spinner} />
                        <span>{loadingLabel}</span>
                      </>
                    ) : (
                      <span>{submitLabel}</span>
                    )}
                  </button>
                </div>
              )}

              {step === "email" && !msgSuccess && (
                <div className={styles.buttonGroup}>
                  <button
                    type="button"
                    className={`${styles.button2} ${loading ? styles.buttonOpacity : ""}`}
                    disabled={loading}
                    onClick={handleChangeCpf}
                  >
                    Alterar CPF
                  </button>
                </div>
              )}

              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={`${styles.button2} ${loading ? styles.buttonOpacity : ""}`}
                  disabled={loading}
                  onClick={() => navigate("/login")}
                >
                  Voltar para Login
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecuperarSenha;
